import { StateCreator } from 'zustand';
import { nanoid } from 'nanoid';
import type { Character, Characteristics, ManualAdjustment } from '../../../types/character';
import { createDefault } from '../utils/defaults';
import { characterRulebookService } from '../../../services/CharacterRulebookService';
import {
  calculateDerivedStats,
  migrateCharacterToProgression,
  recalculateProgressionStats
} from '../utils/calculations';
import { ProficiencyService } from '../../../services/ProficiencyService';
import type { CharacterProficiencies } from '../../../types/proficiency';

export interface CharacterSlice {
  character: Character | null;
  isLoading: boolean;
  lastSaved: Date | null;
  hasUnsavedChanges: boolean;
  validationErrors: string[];
  aimBonus: number;

  setAimBonus: (bonus: number) => void;
  toggleFavouriteAction: (actionId: string) => void;
  getProficiencies: () => CharacterProficiencies;

  create: (name: string) => void;
  createWithRulebooks: (name: string, enabledRulebooks?: string[]) => void;
  update: (updates: Partial<Character>) => void;
  updateCharacteristics: (characteristics: Partial<Characteristics>) => void;
  addManualAdjustment: (adjustment: Omit<ManualAdjustment, 'id' | 'timestamp'>) => void;
  undoManualAdjustment: (adjustmentId: string) => void;
  setEnabledRulebooks: (rulebooks: string[]) => void;
  reset: () => void;
  load: (character: Character) => void;
  _setLoading: (loading: boolean) => void;
  _setSaved: () => void;
  _setValidationErrors: (errors: string[]) => void;
}

export const createCharacterSlice: StateCreator<CharacterSlice> = (set, get) => ({
  character: null,
  isLoading: false,
  lastSaved: null,
  hasUnsavedChanges: false,
  validationErrors: [],
  aimBonus: 0,

  setAimBonus: (bonus: number) => {
    const { character } = get();
    if (!character) return;

    const updatedPreferences = {
      ...(character.combatPreferences || { favouritedActions: [] }),
      aimBonus: bonus
    };

    set({
      character: {
        ...character,
        combatPreferences: updatedPreferences,
        lastModified: new Date()
      },
      hasUnsavedChanges: true,
      aimBonus: bonus // Keep local state in sync if needed, though components seem to read from character
    });
  },

  toggleFavouriteAction: (actionId: string) => {
    const { character } = get();
    if (!character) return;

    const currentFavourites = character.combatPreferences?.favouritedActions || [];
    const newFavourites = currentFavourites.includes(actionId)
      ? currentFavourites.filter((id) => id !== actionId)
      : [...currentFavourites, actionId];

    const updatedPreferences = {
      ...(character.combatPreferences || { aimBonus: 0 }),
      favouritedActions: newFavourites
    };

    set({
      character: {
        ...character,
        combatPreferences: updatedPreferences,
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  getProficiencies: () => {
    const { character } = get();
    if (!character) {
      return {
        weapons: { types: [], classes: [], universal: [], exotic: [] },
        armor: []
      };
    }
    return ProficiencyService.calculateProficiencies(character.talents);
  },

  create: (name: string) => {
    const character = createDefault(name);
    const derivedStats = calculateDerivedStats(character);

    set({
      character: { ...character, derivedStats },
      hasUnsavedChanges: true,
      validationErrors: []
    });
  },

  createWithRulebooks: (name: string, enabledRulebooks?: string[]) => {
    let character = createDefault(name);

    if (enabledRulebooks) {
      character = characterRulebookService.updateCharacterRulebooks(character, enabledRulebooks);
    } else {
      character = characterRulebookService.initializeCharacterRulebooks(character);
    }

    const derivedStats = calculateDerivedStats(character);

    set({
      character: { ...character, derivedStats },
      hasUnsavedChanges: true,
      validationErrors: []
    });
  },

  update: (updates: Partial<Character>) => {
    const { character } = get();
    if (!character) return;

    const updated = {
      ...character,
      ...updates,
      lastModified: new Date()
    };

    const derivedStats = updates.characteristics
      ? calculateDerivedStats(updated)
      : updated.derivedStats;

    const finalCharacter = { ...updated, derivedStats };

    try {
      localStorage.setItem('rogue-trader-current-character', JSON.stringify(finalCharacter));
    } catch (error) {
      console.warn('Failed to auto-save character to localStorage:', error);
    }

    set({
      character: finalCharacter,
      hasUnsavedChanges: true
    });
  },

  updateCharacteristics: (characteristics: Partial<Characteristics>) => {
    const { character } = get();
    if (!character) return;

    const updatedCharacteristics = {
      ...character.characteristics,
      ...characteristics
    };

    let updated = {
      ...character,
      characteristics: updatedCharacteristics,
      lastModified: new Date()
    };

    const derivedStats = calculateDerivedStats(updated);
    updated = { ...updated, derivedStats };

    // Also update progression tracking if it exists
    if (updated.progression) {
      updated = recalculateProgressionStats(updated);
    }

    set({
      character: updated,
      hasUnsavedChanges: true
    });
  },

  addManualAdjustment: (adjustment: Omit<ManualAdjustment, 'id' | 'timestamp'>) => {
    const { character } = get();
    if (!character) return;

    const newAdjustment: ManualAdjustment = {
      ...adjustment,
      id: nanoid(),
      timestamp: new Date()
    };

    set({
      character: {
        ...character,
        manualAdjustments: [...character.manualAdjustments, newAdjustment],
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  undoManualAdjustment: (adjustmentId: string) => {
    const { character } = get();
    if (!character) return;

    const adjustment = character.manualAdjustments.find((adj) => adj.id === adjustmentId);
    if (!adjustment) return;

    // Reverse the adjustment by applying the old value
    const fieldPath = adjustment.field.split('.');
    const updated = { ...character };

    if (fieldPath[0] === 'characteristics' && fieldPath[1]) {
      const characteristic = fieldPath[1] as keyof Characteristics;
      updated.characteristics = {
        ...updated.characteristics,
        [characteristic]: parseInt(String(adjustment.oldValue))
      };
      // Recalculate derived stats
      updated.derivedStats = calculateDerivedStats(updated);
    } else if (fieldPath[0] === 'experience' && fieldPath[1] === 'total') {
      const oldTotal = parseInt(String(adjustment.oldValue));
      updated.experience = {
        ...updated.experience,
        total: oldTotal,
        available: oldTotal - updated.experience.spent
      };
    }

    // Remove the adjustment record
    updated.manualAdjustments = updated.manualAdjustments.filter((adj) => adj.id !== adjustmentId);
    updated.lastModified = new Date();

    set({
      character: updated,
      hasUnsavedChanges: true
    });
  },

  setEnabledRulebooks: (rulebooks: string[]) => {
    const { character } = get();
    if (!character) return;

    set({
      character: {
        ...character,
        enabledRulebooks: rulebooks,
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  reset: () => {
    set({
      character: null,
      isLoading: false,
      lastSaved: null,
      hasUnsavedChanges: false,
      validationErrors: []
    });
  },

  load: (character: Character) => {
    // Migrate v1 characters to v2 with progression tracking.
    // Armour is NOT reconciled here: its protective stats are derived from the
    // catalogue at read time by ArmorResolutionService, so the stored copy
    // cannot go stale and there is nothing to repair.
    const migratedCharacter = migrateCharacterToProgression(character);

    set({
      character: migratedCharacter,
      isLoading: false,
      lastSaved: new Date(),
      hasUnsavedChanges: migratedCharacter !== character, // Mark as unsaved if migration occurred
      validationErrors: []
    });

    // If migration occurred, save to localStorage
    if (migratedCharacter !== character) {
      localStorage.setItem('rogue-trader-current-character', JSON.stringify(migratedCharacter));
    }
  },

  _setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },

  _setSaved: () => {
    set({
      lastSaved: new Date(),
      hasUnsavedChanges: false
    });
  },

  _setValidationErrors: (errors: string[]) => {
    set({ validationErrors: errors });
  }
});
